import React from 'react';
import {
  Autocomplete,
  Box,
  Typography,
  Paper,
  Alert,
  Grid,
  LinearProgress,
  Chip,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Stack,
  Skeleton,
  InputAdornment
} from '@mui/material';
import PeopleIcon from '@mui/icons-material/People';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BarChartIcon from '@mui/icons-material/BarChart';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import SearchIcon from '@mui/icons-material/Search';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { DataGrid, GridToolbarContainer, ExportCsv } from '@mui/x-data-grid';
import { getFacultyLabs, getFacultyResultsSummary } from '../../services/labResults.api';

/** @param {Array<Record<string, unknown>>} labs */
function findLabByParam(labs, param) {
  const p = String(param || '').trim();
  if (!p || !Array.isArray(labs)) return null;
  return labs.find((l) => String(l._id) === p || String(l.labId) === p) ?? null;
}

/** @param {unknown[]} labs */
function sortLabsByCreatedDesc(labs) {
  return [...labs].sort((a, b) => {
    const ta = new Date(a.createdAt || 0).getTime();
    const tb = new Date(b.createdAt || 0).getTime();
    return tb - ta;
  });
}

function getLabOptionLabel(option) {
  if (!option || typeof option !== 'object') return '';
  if (typeof option.title === 'string' && option.title.trim()) return option.title.trim();
  return String(option.labId || option._id || '');
}

function labSearchHaystack(option) {
  if (!option || typeof option !== 'object') return '';
  return [option.title, option.labId, option._id]
    .filter((x) => x != null && String(x).trim())
    .map((x) => String(x).toLowerCase())
    .join(' ');
}

const PAGE_BG = '#f7f9fb';

const emptySummary = {
  totalStudents: 0,
  evaluated: 0,
  pending: 0,
  running: 0,
  failed: 0,
  avgScore: 0,
  passRate: 0
};

const EVAL_STATUSES = ['PENDING', 'RUNNING', 'COMPLETED', 'FAILED'];

/** @param {number} v 0–100 */
function metricHue(v) {
  if (v < 40) return '#d32f2f';
  if (v <= 70) return '#ed6c02';
  return '#2e7d32';
}

function statusChipProps(status) {
  const s = String(status || '').toUpperCase();
  if (s === 'COMPLETED') return { color: 'success', variant: 'filled' };
  if (s === 'FAILED') return { color: 'error', variant: 'filled' };
  if (s === 'RUNNING') return { color: 'info', variant: 'filled' };
  return { color: 'default', variant: 'outlined' };
}

function scoreChipColor(score) {
  if (score == null || !Number.isFinite(Number(score))) return 'default';
  const n = Number(score);
  if (n < 40) return 'error';
  if (n <= 70) return 'warning';
  return 'success';
}

function labStatusChip(labStatus) {
  const s = String(labStatus || '').toUpperCase();
  if (s === 'CLOSED') return { label: 'CLOSED', color: 'default' };
  if (s === 'ACTIVE') return { label: 'ACTIVE', color: 'success' };
  if (s === 'DRAFT') return { label: 'DRAFT', color: 'warning' };
  return { label: s || '—', color: 'default' };
}

const cardSx = {
  borderRadius: 3,
  boxShadow: '0 1px 4px rgba(15, 23, 42, 0.06)',
  border: '1px solid',
  borderColor: 'divider',
  overflow: 'hidden'
};

function KpiCard({ icon, label, valueText, progress, progressColor }) {
  const pct = Math.max(0, Math.min(100, Number(progress) || 0));
  const barColor = progressColor || 'primary.main';
  return (
    <Paper elevation={0} sx={{ ...cardSx, p: 2.5, height: '100%' }}>
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
        <Box sx={{ color: 'text.secondary', pt: 0.25 }}>{icon}</Box>
      </Stack>
      <Typography variant="h4" sx={{ mt: 1, fontWeight: 700, letterSpacing: '-0.02em' }}>
        {valueText}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
        {label}
      </Typography>
      <LinearProgress
        variant="determinate"
        value={pct}
        sx={{
          mt: 2,
          height: 6,
          borderRadius: 99,
          bgcolor: 'action.hover',
          '& .MuiLinearProgress-bar': { borderRadius: 99, bgcolor: barColor }
        }}
      />
    </Paper>
  );
}

/** Must render inside DataGrid (uses grid API). `csvFileBase` without `.csv`. */
function FacultyResultsGridToolbar(props) {
  const { csvFileBase = 'lab-students' } = props;
  return (
    <GridToolbarContainer
      sx={{
        justifyContent: 'flex-end',
        alignItems: 'center',
        gap: 1,
        py: 1,
        px: 0.5,
        borderBottom: 1,
        borderColor: 'divider',
        minHeight: 'auto'
      }}
    >
      <ExportCsv
        variant="outlined"
        size="small"
        startIcon={<FileDownloadOutlinedIcon fontSize="small" />}
        options={{
          fileName: csvFileBase,
          utf8WithBom: true
        }}
      >
        Export CSV
      </ExportCsv>
    </GridToolbarContainer>
  );
}

function DashboardSkeleton() {
  return (
    <Stack spacing={3}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={2}>
        <Box sx={{ flex: 1 }}>
          <Skeleton variant="text" width="55%" height={40} />
          <Skeleton variant="text" width="40%" height={24} />
        </Box>
        <Skeleton variant="rounded" width={88} height={32} sx={{ borderRadius: 2 }} />
      </Stack>
      <Grid container spacing={3}>
        {[1, 2, 3, 4].map((k) => (
          <Grid key={k} item xs={12} sm={6} md={3}>
            <Skeleton variant="rounded" height={140} sx={{ borderRadius: 3 }} />
          </Grid>
        ))}
      </Grid>
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Skeleton variant="rounded" height={380} sx={{ borderRadius: 3 }} />
        </Grid>
        <Grid item xs={12} md={4}>
          <Skeleton variant="rounded" height={380} sx={{ borderRadius: 3 }} />
        </Grid>
      </Grid>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Skeleton variant="rounded" height={300} sx={{ borderRadius: 3 }} />
        </Grid>
        <Grid item xs={12} md={6}>
          <Skeleton variant="rounded" height={300} sx={{ borderRadius: 3 }} />
        </Grid>
      </Grid>
      <Skeleton variant="rounded" height={420} sx={{ borderRadius: 3 }} />
    </Stack>
  );
}

export default function FacultyLabResultsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const labIdFromUrl = (searchParams.get('labId') || '').trim();

  const [labs, setLabs] = React.useState([]);
  const [labsLoading, setLabsLoading] = React.useState(true);
  const [labsError, setLabsError] = React.useState('');

  const [lab, setLab] = React.useState(null);
  const [summary, setSummary] = React.useState(emptySummary);
  const [students, setStudents] = React.useState([]);
  const [distribution, setDistribution] = React.useState([]);
  const [componentStats, setComponentStats] = React.useState({
    git: { avg: 0, max: 0, failRate: 0 },
    api: { avg: 0, max: 0, failRate: 0 },
    ui: { avg: 0, max: 0, failRate: 0 }
  });
  const [error, setError] = React.useState('');
  const [loading, setLoading] = React.useState(false);

  const [nameSearch, setNameSearch] = React.useState('');
  const [statusFilter, setStatusFilter] = React.useState('ALL');

  const matchedLab = React.useMemo(() => findLabByParam(labs, labIdFromUrl), [labs, labIdFromUrl]);
  const selectedLabId = matchedLab ? String(matchedLab._id) : '';

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      setLabsLoading(true);
      setLabsError('');
      try {
        const raw = await getFacultyLabs();
        if (cancelled) return;
        setLabs(sortLabsByCreatedDesc(raw));
      } catch (e) {
        if (!cancelled) {
          setLabs([]);
          setLabsError(e?.response?.data?.message || e?.message || 'Failed to load labs');
        }
      } finally {
        if (!cancelled) setLabsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  /** Normalize `labId` query to a Mongo `_id`, or repair invalid values. */
  React.useEffect(() => {
    if (labsLoading) return;
    const raw = labIdFromUrl;
    if (!raw) return;
    const found = findLabByParam(labs, raw);
    if (found) {
      const canonical = String(found._id);
      if (raw !== canonical) {
        setSearchParams({ labId: canonical }, { replace: true });
      }
      return;
    }
    if (labs.length > 0) {
      setSearchParams({ labId: String(labs[0]._id) }, { replace: true });
    }
    // If labs failed to load or the list is empty, keep the URL unchanged (avoid wiping a valid deep link).
  }, [labsLoading, labs, labIdFromUrl, setSearchParams]);

  React.useEffect(() => {
    if (labsLoading) return;

    if (!labIdFromUrl) {
      setError('');
      setLoading(false);
      setLab(null);
      setSummary(emptySummary);
      setStudents([]);
      setDistribution([]);
      setComponentStats({
        git: { avg: 0, max: 0, failRate: 0 },
        api: { avg: 0, max: 0, failRate: 0 },
        ui: { avg: 0, max: 0, failRate: 0 }
      });
      return;
    }

    const found = findLabByParam(labs, labIdFromUrl);
    if (!found) {
      return;
    }

    const summaryId = String(found._id);
    let cancelled = false;

    (async () => {
      setLoading(true);
      setError('');
      try {
        const res = await getFacultyResultsSummary(summaryId);
        if (cancelled) return;
        const labPayload = res?.lab && typeof res.lab === 'object' ? res.lab : null;
        setLab(
          labPayload
            ? {
                title: typeof labPayload.title === 'string' ? labPayload.title : 'Lab',
                status: typeof labPayload.status === 'string' ? labPayload.status : 'DRAFT',
                labCode: typeof labPayload.labCode === 'string' ? labPayload.labCode : ''
              }
            : null
        );
        setSummary({
          totalStudents: Number(res?.summary?.totalStudents) || 0,
          evaluated: Number(res?.summary?.evaluated) || 0,
          pending: Number(res?.summary?.pending) || 0,
          running: Number(res?.summary?.running) || 0,
          failed: Number(res?.summary?.failed) || 0,
          avgScore: Number(res?.summary?.avgScore) || 0,
          passRate: Number(res?.summary?.passRate) || 0
        });
        setStudents(Array.isArray(res?.students) ? res.students : []);
        setDistribution(Array.isArray(res?.distribution) ? res.distribution : []);
        setComponentStats({
          git: {
            avg: Number(res?.componentStats?.git?.avg) || 0,
            max: Number(res?.componentStats?.git?.max) || 0,
            failRate: Number(res?.componentStats?.git?.failRate) || 0
          },
          api: {
            avg: Number(res?.componentStats?.api?.avg) || 0,
            max: Number(res?.componentStats?.api?.max) || 0,
            failRate: Number(res?.componentStats?.api?.failRate) || 0
          },
          ui: {
            avg: Number(res?.componentStats?.ui?.avg) || 0,
            max: Number(res?.componentStats?.ui?.max) || 0,
            failRate: Number(res?.componentStats?.ui?.failRate) || 0
          }
        });
      } catch (e) {
        if (!cancelled) {
          setError(e?.response?.data?.message || e?.message || 'Failed to load');
          setLab(null);
          setSummary(emptySummary);
          setStudents([]);
          setDistribution([]);
          setComponentStats({
            git: { avg: 0, max: 0, failRate: 0 },
            api: { avg: 0, max: 0, failRate: 0 },
            ui: { avg: 0, max: 0, failRate: 0 }
          });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [labsLoading, labs, labIdFromUrl]);

  const passFailPieData = React.useMemo(() => {
    let pass = 0;
    let fail = 0;
    for (const s of students) {
      if (String(s.status).toUpperCase() !== 'COMPLETED') continue;
      if (s.score == null || !Number.isFinite(Number(s.score))) continue;
      const n = Number(s.score);
      if (n >= 40) pass += 1;
      else fail += 1;
    }
    return [
      { name: 'Pass', value: pass, fill: '#2e7d32' },
      { name: 'Fail', value: fail, fill: '#c62828' }
    ];
  }, [students]);

  const componentChartData = React.useMemo(
    () => [
      { component: 'Git', avg: componentStats.git.avg, max: componentStats.git.max, failRate: componentStats.git.failRate },
      { component: 'API', avg: componentStats.api.avg, max: componentStats.api.max, failRate: componentStats.api.failRate },
      { component: 'UI', avg: componentStats.ui.avg, max: componentStats.ui.max, failRate: componentStats.ui.failRate }
    ],
    [componentStats]
  );

  const statusBreakdownData = React.useMemo(
    () => [
      { label: 'Pending', count: summary.pending, fill: '#9e9e9e' },
      { label: 'Running', count: summary.running, fill: '#0288d1' },
      { label: 'Completed', count: summary.evaluated, fill: '#2e7d32' },
      { label: 'Failed', count: summary.failed, fill: '#c62828' }
    ],
    [summary.pending, summary.running, summary.evaluated, summary.failed]
  );

  const gridRows = React.useMemo(() => {
    const q = nameSearch.trim().toLowerCase();
    return students.filter((s) => {
      const name = String(s.name || s.studentId || '').toLowerCase();
      if (q && !name.includes(q)) return false;
      if (statusFilter !== 'ALL' && String(s.status || '').toUpperCase() !== statusFilter) return false;
      return true;
    });
  }, [students, nameSearch, statusFilter]);

  const avgProgressColor = React.useMemo(() => metricHue(summary.avgScore), [summary.avgScore]);
  const passProgressColor = React.useMemo(() => metricHue(summary.passRate), [summary.passRate]);

  const evaluatedRatio = React.useMemo(() => {
    const t = summary.totalStudents || 0;
    if (!t) return 0;
    return (summary.evaluated / t) * 100;
  }, [summary.totalStudents, summary.evaluated]);

  const columns = React.useMemo(
    () => [
      {
        field: 'name',
        headerName: 'Name',
        flex: 1.2,
        minWidth: 160,
        valueGetter: (value, row) => row.name || row.studentId
      },
      {
        field: 'rollNumber',
        headerName: 'Roll',
        width: 120,
        valueGetter: (value, row) => (row.rollNumber != null && String(row.rollNumber).trim() ? String(row.rollNumber).trim() : null),
        renderCell: (params) => (params.value ? String(params.value) : '—')
      },
      {
        field: 'setKey',
        headerName: 'Set',
        width: 100,
        valueGetter: (value, row) => (row.setKey != null && String(row.setKey).trim() ? String(row.setKey).trim() : null),
        renderCell: (params) => (params.value ? String(params.value) : '—')
      },
      {
        field: 'score',
        headerName: 'Score',
        width: 110,
        align: 'center',
        headerAlign: 'center',
        type: 'number',
        sortComparator: (v1, v2) => {
          const a = v1 == null || Number.isNaN(v1) ? -Infinity : Number(v1);
          const b = v2 == null || Number.isNaN(v2) ? -Infinity : Number(v2);
          return a - b;
        },
        valueGetter: (value, row) => (row.score == null || !Number.isFinite(Number(row.score)) ? null : Number(row.score)),
        renderCell: (params) => {
          const v = params.value;
          if (v == null) return <Chip size="small" label="—" variant="outlined" />;
          return <Chip size="small" label={v} color={scoreChipColor(v)} variant="filled" />;
        }
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 130,
        renderCell: (params) => {
          const st = String(params.value || 'PENDING').toUpperCase();
          const chip = statusChipProps(st);
          return <Chip size="small" label={st} color={chip.color} variant={chip.variant} />;
        }
      },
      {
        field: 'gitScore',
        headerName: 'Git',
        width: 88,
        type: 'number',
        align: 'right',
        headerAlign: 'right',
        valueGetter: (value, row) => (row.gitScore == null ? null : Number(row.gitScore))
      },
      {
        field: 'apiScore',
        headerName: 'API',
        width: 88,
        type: 'number',
        align: 'right',
        headerAlign: 'right',
        valueGetter: (value, row) => (row.apiScore == null ? null : Number(row.apiScore))
      },
      {
        field: 'uiScore',
        headerName: 'UI',
        width: 88,
        type: 'number',
        align: 'right',
        headerAlign: 'right',
        valueGetter: (value, row) => (row.uiScore == null ? null : Number(row.uiScore))
      },
      {
        field: 'lastSubmission',
        headerName: 'Last submission',
        flex: 1,
        minWidth: 180,
        valueGetter: (value, row) => row.lastSubmission || null,
        renderCell: (params) => {
          const raw = params.value;
          if (!raw) return '—';
          try {
            return new Date(raw).toLocaleString();
          } catch {
            return '—';
          }
        }
      }
    ],
    []
  );

  const labIdForQuery = selectedLabId || labIdFromUrl;

  const onRowClick = React.useCallback(
    (params) => {
      const sid = params.row.submissionId != null ? String(params.row.submissionId).trim() : '';
      if (!sid) return;
      const q = labIdForQuery ? `?labId=${encodeURIComponent(labIdForQuery)}` : '';
      navigate(`/faculty/lab-results/${sid}${q}`);
    },
    [navigate, labIdForQuery]
  );

  const onLabAutocompleteChange = React.useCallback(
    (_event, option) => {
      if (option && option._id != null) {
        setSearchParams({ labId: String(option._id) });
      } else {
        setSearchParams({});
      }
    },
    [setSearchParams]
  );

  const labTitle = lab?.title || 'Faculty lab results';
  const labChip = labStatusChip(lab?.status);
  const pieTotal = passFailPieData[0].value + passFailPieData[1].value;
  const showEmptyData = !loading && labIdFromUrl && matchedLab && !error && summary.totalStudents === 0;

  const csvExportFileBase = React.useMemo(() => {
    const parts = [lab?.labCode, lab?.title].filter(Boolean);
    const raw = parts.length ? parts.join('-') : labIdForQuery || 'lab-results';
    const safe = String(raw)
      .replace(/[^a-zA-Z0-9-_]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .slice(0, 80);
    return `${safe || 'lab-results'}_students`;
  }, [lab?.labCode, lab?.title, labIdForQuery]);

  const dropdownDisabled = labsLoading || loading;

  const labOptionChip = (status) => {
    const s = String(status || '').toUpperCase();
    if (s === 'ACTIVE') return <Chip size="small" label="ACTIVE" color="success" sx={{ ml: 1, height: 22 }} />;
    if (s === 'CLOSED') return <Chip size="small" label="CLOSED" color="default" variant="outlined" sx={{ ml: 1, height: 22 }} />;
    if (s === 'DRAFT') return <Chip size="small" label="DRAFT" color="warning" variant="outlined" sx={{ ml: 1, height: 22 }} />;
    return null;
  };

  return (
    <Box sx={{ bgcolor: PAGE_BG, minHeight: '100vh', pb: 4 }}>
      <Box sx={{ maxWidth: 1400, mx: 'auto', p: 3 }}>
        {labsError ? (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
            {labsError}
          </Alert>
        ) : null}

        {error && labIdFromUrl ? (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
            {error}
          </Alert>
        ) : null}

        {labsLoading ? (
          <DashboardSkeleton />
        ) : (
          <Stack spacing={3}>
            <Paper elevation={0} sx={{ ...cardSx, p: 2 }}>
              <Autocomplete
                options={labs}
                value={matchedLab}
                onChange={onLabAutocompleteChange}
                getOptionLabel={getLabOptionLabel}
                isOptionEqualToValue={(a, b) => Boolean(a && b && String(a._id) === String(b._id))}
                disabled={dropdownDisabled}
                size="small"
                fullWidth
                sx={{ maxWidth: { sm: 560 } }}
                clearOnEscape
                filterOptions={(options, { inputValue }) => {
                  const q = inputValue.trim().toLowerCase();
                  if (!q) return options;
                  return options.filter((o) => labSearchHaystack(o).includes(q));
                }}
                renderOption={(props, option) => (
                  <li {...props}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ width: '100%', pr: 0.5 }}>
                      <Typography noWrap sx={{ fontWeight: 500 }}>
                        {getLabOptionLabel(option)}
                      </Typography>
                      {labOptionChip(option.status)}
                    </Stack>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField {...params} label="Select Lab" placeholder="Search by title, lab code, or id" />
                )}
              />
            </Paper>

            {!labIdFromUrl ? (
              <Paper elevation={0} sx={{ ...cardSx, p: 6, textAlign: 'center' }}>
                <Typography variant="h6" color="text.secondary">
                  Select a lab to view results
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Choose a lab from the dropdown above. The address bar will stay in sync with your selection.
                </Typography>
              </Paper>
            ) : loading ? (
              <DashboardSkeleton />
            ) : (
          <Stack spacing={3}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              alignItems={{ xs: 'flex-start', sm: 'flex-start' }}
              justifyContent="space-between"
              spacing={2}
            >
              <Box>
                <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
                  {labTitle}
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
                  Evaluation analytics and student performance
                </Typography>
                {lab?.labCode ? (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    {lab.labCode}
                  </Typography>
                ) : null}
              </Box>
              {lab ? <Chip size="small" label={labChip.label} color={labChip.color} sx={{ fontWeight: 600 }} /> : null}
            </Stack>

            {showEmptyData ? (
              <Paper elevation={0} sx={{ ...cardSx, p: 6, textAlign: 'center' }}>
                <Typography variant="h6" color="text.secondary">
                  No evaluation data available
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  No students are enrolled for this lab yet.
                </Typography>
              </Paper>
            ) : (
              <>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6} md={3}>
                    <KpiCard
                      icon={<PeopleIcon />}
                      label="Total students"
                      valueText={summary.totalStudents}
                      progress={100}
                      progressColor="primary.main"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <KpiCard
                      icon={<CheckCircleIcon />}
                      label="Evaluated"
                      valueText={summary.evaluated}
                      progress={evaluatedRatio}
                      progressColor="success.main"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <KpiCard
                      icon={<BarChartIcon />}
                      label="Average score"
                      valueText={summary.evaluated > 0 ? summary.avgScore : '—'}
                      progress={summary.evaluated > 0 ? summary.avgScore : 0}
                      progressColor={avgProgressColor}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <KpiCard
                      icon={<TrendingUpIcon />}
                      label="Pass rate"
                      valueText={summary.evaluated > 0 ? `${summary.passRate}%` : '—'}
                      progress={summary.evaluated > 0 ? summary.passRate : 0}
                      progressColor={passProgressColor}
                    />
                  </Grid>
                </Grid>

                <Grid container spacing={3}>
                  <Grid item xs={12} md={8}>
                    <Paper elevation={0} sx={{ ...cardSx, p: 2.5, height: 420 }}>
                      <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
                        Score distribution
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                        Completed evaluations by score band
                      </Typography>
                      <ResponsiveContainer width="100%" height="88%">
                        <BarChart data={distribution} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e0e0e0" />
                          <XAxis dataKey="range" tick={{ fontSize: 12 }} />
                          <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                          <Tooltip
                            contentStyle={{ borderRadius: 8, border: '1px solid #e0e0e0' }}
                            formatter={(value) => [value, 'Students']}
                          />
                          <Bar dataKey="count" name="Students" fill="#1976d2" radius={[6, 6, 0, 0]} maxBarSize={56} />
                        </BarChart>
                      </ResponsiveContainer>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Paper elevation={0} sx={{ ...cardSx, p: 2.5, height: 420, display: 'flex', flexDirection: 'column' }}>
                      <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>
                        Pass vs fail
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                        Among completed (score ≥ 40 = pass)
                      </Typography>
                      <Box sx={{ flex: 1, minHeight: 280 }}>
                        {pieTotal === 0 ? (
                          <Stack alignItems="center" justifyContent="center" sx={{ height: '100%' }}>
                            <Typography variant="body2" color="text.secondary">
                              No completed scores to chart
                            </Typography>
                          </Stack>
                        ) : (
                          <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                              <Pie
                                data={passFailPieData}
                                dataKey="value"
                                nameKey="name"
                                cx="50%"
                                cy="50%"
                                innerRadius="58%"
                                outerRadius="82%"
                                paddingAngle={2}
                              >
                                {passFailPieData.map((entry) => (
                                  <Cell key={entry.name} fill={entry.fill} />
                                ))}
                              </Pie>
                              <Tooltip formatter={(value, name) => [`${value} students`, name]} />
                              <Legend verticalAlign="bottom" height={28} />
                            </PieChart>
                          </ResponsiveContainer>
                        )}
                      </Box>
                    </Paper>
                  </Grid>
                </Grid>

                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Paper elevation={0} sx={{ ...cardSx, p: 2.5, height: 320 }}>
                      <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
                        Component stats
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                        Git / API / UI — avg, max, fail rate %
                      </Typography>
                      <ResponsiveContainer width="100%" height="78%">
                        <BarChart data={componentChartData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e0e0e0" />
                          <XAxis dataKey="component" tick={{ fontSize: 12 }} />
                          <YAxis tick={{ fontSize: 12 }} />
                          <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid #e0e0e0' }} />
                          <Legend />
                          <Bar dataKey="avg" fill="#2e7d32" name="Avg" radius={[4, 4, 0, 0]} maxBarSize={28} />
                          <Bar dataKey="max" fill="#ed6c02" name="Max" radius={[4, 4, 0, 0]} maxBarSize={28} />
                          <Bar dataKey="failRate" fill="#c62828" name="Fail rate %" radius={[4, 4, 0, 0]} maxBarSize={28} />
                        </BarChart>
                      </ResponsiveContainer>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Paper elevation={0} sx={{ ...cardSx, p: 2.5, height: 320 }}>
                      <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
                        Evaluation status breakdown
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                        PENDING / RUNNING / COMPLETED / FAILED
                      </Typography>
                      <ResponsiveContainer width="100%" height="78%">
                        <BarChart data={statusBreakdownData} layout="vertical" margin={{ top: 4, right: 24, left: 8, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#e0e0e0" />
                          <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12 }} />
                          <YAxis type="category" dataKey="label" width={88} tick={{ fontSize: 12 }} />
                          <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid #e0e0e0' }} />
                          <Bar dataKey="count" radius={[0, 6, 6, 0]} maxBarSize={28}>
                            {statusBreakdownData.map((entry) => (
                              <Cell key={entry.label} fill={entry.fill} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </Paper>
                  </Grid>
                </Grid>

                <Paper elevation={0} sx={{ ...cardSx, p: 2.5 }}>
                  <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>
                    Students
                  </Typography>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }} alignItems={{ sm: 'center' }}>
                    <TextField
                      size="small"
                      placeholder="Search by name"
                      value={nameSearch}
                      onChange={(e) => setNameSearch(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon fontSize="small" color="action" />
                          </InputAdornment>
                        )
                      }}
                      sx={{ minWidth: { sm: 260 }, flex: 1 }}
                    />
                    <FormControl size="small" sx={{ minWidth: 180 }}>
                      <InputLabel>Status</InputLabel>
                      <Select
                        label="Status"
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                      >
                        <MenuItem value="ALL">All statuses</MenuItem>
                        {EVAL_STATUSES.map((s) => (
                          <MenuItem key={s} value={s}>
                            {s}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Stack>
                  <Box
                    sx={{
                      width: '100%',
                      height: 520,
                      '& .MuiDataGrid-row:not(.st-row-no-sub)': { cursor: 'pointer' },
                      '& .MuiDataGrid-row.st-row-no-sub': { cursor: 'default', opacity: 0.85 }
                    }}
                  >
                    <DataGrid
                      rows={gridRows}
                      columns={columns}
                      getRowId={(row) => row.studentId}
                      getRowClassName={(params) =>
                        params.row.submissionId != null && String(params.row.submissionId).trim()
                          ? ''
                          : 'st-row-no-sub'
                      }
                      showToolbar
                      slots={{ toolbar: FacultyResultsGridToolbar }}
                      slotProps={{
                        toolbar: { csvFileBase: csvExportFileBase }
                      }}
                      disableRowSelectionOnClick
                      initialState={{
                        sorting: {
                          sortModel: [{ field: 'score', sort: 'desc' }]
                        }
                      }}
                      pageSizeOptions={[10, 25, 50]}
                      onRowClick={onRowClick}
                      sx={{
                        border: 'none',
                        borderRadius: 2,
                        '& .MuiDataGrid-columnHeaders': { bgcolor: 'action.hover', borderRadius: '8px 8px 0 0' },
                        '& .MuiDataGrid-cell:focus': { outline: 'none' },
                        '& .MuiDataGrid-cell:focus-within': { outline: 'none' }
                      }}
                    />
                  </Box>
                </Paper>
              </>
            )}
          </Stack>
          )}
        </Stack>
        )}
      </Box>
    </Box>
  );
}
